import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';

import { ModalHOC } from 'common/HOCs/ModalHOC';
import { NoItemsPanel } from 'common/components';
import { taggedStylesToString, withTheme } from 'common/styling/theme';

import { change } from 'redux-form';
// import { BasketIcon } from 'common/icons';
import FormFieldsComposer from '../FormFieldsComposer';

import { getDynamicStyles, staticStyles } from './style';

class GenerateArrayField extends Component {
  // 深拷贝函数，不会序列化函数
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }
    if (obj instanceof Date) {
      return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
      return obj.map(item => GenerateArrayField.deepClone(item));
    }
    if (typeof obj === 'object') {
      const clonedObj = {};
      Object.keys(obj).forEach(key => {
        clonedObj[key] = GenerateArrayField.deepClone(obj[key]);
      });
      return clonedObj;
    }
  }

  // 动态创建包含condition字段的schema
  static createDynamicSchema(originalSchema) {
    if (!originalSchema || !originalSchema.items) {
      return originalSchema;
    }
    // 创建schema的深拷贝
    const dynamicSchema = GenerateArrayField.deepClone(originalSchema);
    // 确保fields对象存在
    if (!dynamicSchema.items.properties) {
      dynamicSchema.items.properties = {};
    }

    // 设置name字段为只读
    if (dynamicSchema.items.properties.name) {
      dynamicSchema.items.properties.name.disabled = true;
    }
    return dynamicSchema;
  }

  // 保留所有字段，包括name字段（已设为只读）
  static filterSchemaFields(schema) {
    if (!schema || !schema.items || !schema.items.fields) return schema;
    // 创建schema的深拷贝
    const filteredSchema = GenerateArrayField.deepClone(schema);
    // 保留所有字段，不再过滤name字段
    return filteredSchema;
  }

  static isValidGroup(group) {
    return (
      (typeof group === 'string' && group.trim().length > 0) || (typeof group === 'number' && Number.isFinite(group))
    );
  }

  static getGroupName(group, groupOptions = []) {
    const option = groupOptions.find(item => String(item.value) === String(group));
    return option ? option.label : String(group);
  }

  static getGroupOptions(schema) {
    const options = schema?.groupOptions || schema?.group_options || [];
    return Array.isArray(options) ? options : [];
  }

  static findRewardAmountItem(rewardAmount, group, groupName) {
    return (
      rewardAmount.find(
        item => item && (String(item.name) === String(group) || String(item.name) === String(groupName))
      ) || {}
    );
  }

  componentDidMount() {
    // 页面初始化时获取所有值
    this.handleNextButtonClick();
  }

  componentDidUpdate(prevProps) {
    // 获取当前和之前的formData
    const currentFormData = this.props.formData;
    const prevFormData = prevProps.formData;
    const targetIndex = this.props.index !== undefined ? this.props.index : 0;

    // 比较关键数据是否变化，避免无限循环
    const currentSelectedGroups = currentFormData?.regular_account?.group_exceptions?.[targetIndex]?.value?.item || [];
    const prevSelectedGroups = prevFormData?.regular_account?.group_exceptions?.[targetIndex]?.value?.item || [];
    const currentRewardAmount =
      currentFormData?.regular_account?.group_exceptions?.[targetIndex]?.value?.reward_amount || [];
    const prevRewardAmount = prevFormData?.regular_account?.group_exceptions?.[targetIndex]?.value?.reward_amount || [];
    const currentRewardNames = currentRewardAmount.map(item => item?.name).filter(Boolean);
    const prevRewardNames = prevRewardAmount.map(item => item?.name).filter(Boolean);
    const groupOptionsChanged =
      JSON.stringify(GenerateArrayField.getGroupOptions(this.props.schema)) !==
      JSON.stringify(GenerateArrayField.getGroupOptions(prevProps.schema));

    // 只有当关键数据变化时才重新初始化
    if (
      JSON.stringify(currentSelectedGroups) !== JSON.stringify(prevSelectedGroups) ||
      JSON.stringify(currentRewardNames) !== JSON.stringify(prevRewardNames) ||
      groupOptionsChanged
    ) {
      this.handleNextButtonClick();
    }
  }

  onDragEnd = result => {
    const { fields } = this.props;

    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;
    const [removed] = fields.getAll().splice(startIndex, 1);

    fields.splice(endIndex, 0, removed);
  };

  // Next按钮
  handleNextButtonClick = index => {
    // 如果传入了index参数，使用传入的值；否则使用props中的index（如果有的话），如果都没有则使用默认值0
    const targetIndex = index !== undefined ? index : this.props.index !== undefined ? this.props.index : 0;
    const { fields, formData, dispatch, formName } = this.props;
    console.log('in-------------------------------');
    // 尝试多种可能的路径获取选中的group items
    let selectedGroups = [];
    // let conditions = [];

    // 主路径：从regular_account.group_exceptions获取
    if (formData?.regular_account?.group_exceptions && formData.regular_account.group_exceptions.length > 0) {
      const selectedException = formData.regular_account.group_exceptions[targetIndex];
      selectedGroups = selectedException?.value?.item || [];
    }
    const rewardAmount = formData?.regular_account?.group_exceptions?.[targetIndex]?.value?.reward_amount;
    const rewardAmountItems = Array.isArray(rewardAmount) ? rewardAmount : [];
    const groupOptions = GenerateArrayField.getGroupOptions(this.props.schema);

    // 清理与spread/percentage类型无关的残留字段，避免出现 value.value 之类的嵌套数据
    const currentType = formData?.regular_account?.group_exceptions?.[targetIndex]?.type;
    if (currentType && currentType !== 'fix') {
      const originalValue = formData?.regular_account?.group_exceptions?.[targetIndex]?.value || {};
      const cleanedValue = {
        item: originalValue.item || [],
        reward_amount: originalValue.reward_amount || [],
      };
      if (JSON.stringify(originalValue) !== JSON.stringify(cleanedValue)) {
        dispatch(change(formName, `regular_account.group_exceptions[${targetIndex}].value`, cleanedValue));
        // 同步更新本地 selectedGroups 使用清洗后的数据
        selectedGroups = cleanedValue.item;
      }
    }

    selectedGroups = Array.isArray(selectedGroups) ? selectedGroups : [];

    // 清空现有的所有字段，重新生成数据
    const currentFields = fields.getAll() || [];
    // 从后往前删除，避免索引变化问题
    for (let i = currentFields.length - 1; i >= 0; i -= 1) {
      fields.remove(i);
    }
    // 为每个选中的group重新创建奖励条目
    selectedGroups.forEach(group => {
      if (GenerateArrayField.isValidGroup(group)) {
        const groupName = GenerateArrayField.getGroupName(group, groupOptions);
        const newItem = {
          name: groupName,
        };
        const existingValue = GenerateArrayField.findRewardAmountItem(rewardAmountItems, group, groupName).value;
        newItem.value = existingValue !== undefined ? existingValue : null;
        fields.push(newItem);
      }
    });
  };

  render() {
    const {
      schema,
      fields,
      meta: { error, pristine },
      formName,
      arraysName,
      submitting,
      theme,
    } = this.props;

    const style = taggedStylesToString(staticStyles);
    const dynamicStyles = Object.keys(theme).length ? taggedStylesToString(getDynamicStyles(theme)) : ` `;

    const formError = !pristine && error;
    const isDragDisabled = !schema.ordering || fields.length === 1;
    const isFormError = typeof error === 'object';

    // 创建动态schema，包含condition字段
    const dynamicSchema = GenerateArrayField.createDynamicSchema(schema);

    // 过滤schema，移除name字段
    const filteredSchema = GenerateArrayField.filterSchemaFields(dynamicSchema);
    return (
      <div className="FieldArray" id={arraysName}>
        <div className="FieldArray__top-line">
          <div className="FieldArray__title">
            <FormattedMessage id={schema.title}>{txt => <h2>{txt}</h2>}</FormattedMessage>
          </div>
        </div>

        {fields.length ? (
          <DragDropContext onDragEnd={this.onDragEnd}>
            <Droppable droppableId="droppable" isDropDisabled={isDragDisabled}>
              {(provided, snapshot) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={classNames('FieldArray__isDragging-inner', { isDraggingOver: snapshot.isDraggingOver })}>
                  {fields.map((item, index) => (
                    <Draggable
                      key={`${index}_${fields.length}`}
                      draggableId={item}
                      index={index}
                      isDragDisabled={isDragDisabled}>
                      {(provided, snapshot) => (
                        <div
                          className={classNames('FieldArray__item-inner', {
                            FieldArray__notDraggable: !schema.ordering,
                            FieldArray__isDraggable: snapshot.isDragging,
                            FieldArray__item_error: isFormError && error[index],
                          })}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={provided.draggableProps.style}>
                          <div className="FieldArray__item">
                            <p className="FieldArray__item-number">{index + 1}</p>
                            <FormFieldsComposer
                              name={`${item}`}
                              schema={filteredSchema.items}
                              formName={formName}
                              submitting={submitting}
                            />
                            {isFormError && error[index] && (
                              <FormattedMessage id={error[index]}>
                                {txt => <span className="ArrayField__item-error">{txt}</span>}
                              </FormattedMessage>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <NoItemsPanel
            // captionSlug={schema.title}
            // descriptionSlug={schema.description}
            className="FieldArray__no-items"
            isError={!!formError}
          />
        )}

        <style>{style}</style>
        <style>{dynamicStyles}</style>
      </div>
    );
  }
}

GenerateArrayField.propTypes = {
  schema: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  showModal: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
  fields: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  arraysName: PropTypes.string.isRequired,
  submitting: PropTypes.bool,
  theme: PropTypes.object,
  formData: PropTypes.object,
  index: PropTypes.number,
};

GenerateArrayField.defaultProps = {
  fields: {},
  submitting: false,
  theme: {},
  formData: {},
  index: 0,
};

// 使用connect从Redux store获取表单数据
const mapStateToProps = (state, ownProps) => {
  const formData = state.form && state.form[ownProps.formName] ? state.form[ownProps.formName].values || {} : {};
  return {
    formData,
  };
};

const ThemedArrayField = compose(connect(mapStateToProps), withTheme(), ModalHOC)(GenerateArrayField);

export default ThemedArrayField;
export { GenerateArrayField };
